/**
 * F17 — wspólny pasek paginacji dla zakładek modułu Procesy.
 *
 * Wyciągnięty ze zduplikowanego markupu w 3 zakładkach (MyPendingDecisionsTab,
 * ActiveInstancesTab, MySubmittedInstancesTab). Zakres `{start}–{end} z {total}`
 * tłumaczony przez t() (wcześniej spójnik „z" był zahardkodowany poza i18n,
 * przez co en.json go nie tłumaczył).
 *
 * Renderuje się tylko gdy `total > limit` (poniżej — null).
 */

import { useTranslation } from 'react-i18next';

export interface PaginationControlsProps {
  total: number;
  offset: number;
  limit: number;
  onOffsetChange: (next: number) => void;
  /**
   * `data-testid` nadpisujący domyślny `'approvals-pagination'` — potrzebne, gdy na jednym
   * ekranie renderuje się WIĘCEJ NIŻ JEDNA instancja tego komponentu naraz (np. mini-paginacja
   * per rozwinięty koszyk grupowania, fala 2 kokpitu decyzji) — bez tego wszystkie instancje
   * dzieliłyby ten sam testid, kolidując w zapytaniach `getByTestId`.
   */
  testId?: string;
}

export function PaginationControls({
  total,
  offset,
  limit,
  onOffsetChange,
  testId = 'approvals-pagination',
}: PaginationControlsProps) {
  const { t } = useTranslation();
  const start = offset + 1;
  const end = Math.min(offset + limit, total);
  const hasPrev = offset > 0;
  const hasNext = end < total;

  if (total <= limit) {
    return null;
  }

  return (
    <div
      className="flex items-center justify-between text-xs text-text-tertiary"
      data-testid={testId}
    >
      <span data-testid={`${testId}-range`}>
        {t('tenant.processes.approvals.pagination.range', { start, end, total })}
      </span>
      <div className="flex gap-1">
        <button
          type="button"
          disabled={!hasPrev}
          onClick={() => onOffsetChange(Math.max(0, offset - limit))}
          className="rounded border border-border-primary px-2 py-1 disabled:opacity-50"
        >
          ←
        </button>
        <button
          type="button"
          disabled={!hasNext}
          onClick={() => onOffsetChange(offset + limit)}
          className="rounded border border-border-primary px-2 py-1 disabled:opacity-50"
        >
          →
        </button>
      </div>
    </div>
  );
}
